import React, { useEffect, useMemo, useState } from 'react'
import { Gem, Loader, XCircle, Package, Edit3 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import cardApi from '../services/cardApi'
import cardwwApi from '../services/cardwwApi'
import cardww2Api from '../services/cardww2Api'
import cardww3Api from '../services/cardww3Api'
import cardww4Api from '../services/cardww4Api'
import cardww5Api from '../services/cardww5Api'
import cardww6Api from '../services/cardww6Api'
import { PAYMENT_METHODS } from '../services/paymentApi'
import { closePreparedPaymentWindow, prepareExternalPaymentWindow } from '../utils/openExternalPaymentLink'

/**
 * CardPayment
 * Универсальная оплата банковскими картами для card1, cardww1, cardww2 и cardww3
 * Поддерживает два режима: готовые пакеты и произвольная сумма
 *
 * Провайдеры:
 *  - CARD    → cardApi    (рубли, РФ)
 *  - CARDWW1 → cardwwApi  (доллары, резервный международный, подпись #2 в UI)
 *  - CARDWW2 → cardww2Api (доллары, основной OverPay,       подпись #1 в UI)
 *  - CARDWW3 → cardww3Api (доллары, провайдер Fride,        подпись #3 в UI)
 *  - CARDWW4 → cardww4Api (доллары, провайдер Softkeys,      «Банк» в UI)
 *  - CARDWW5 → cardww5Api (доллары, провайдер Paytree,       Visa/MC в UI)
 *  - CARDWW6 → cardww6Api (доллары, провайдер EasyPay/Stripe, Visa/MC в UI)
 *  - GOOGLE_PAY → cardww4Api (доллары, провайдер Softkeys,   Google Pay в UI, сейчас скрыт)
 *  - APPLE_PAY  → cardww4Api (доллары, провайдер Softkeys,   Apple Pay в UI, сейчас скрыт)
 */
const CardPayment = ({ method = PAYMENT_METHODS.CARD }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  // Признак "международных карт" — используется для выбора валюты (доллары vs рубли)
  const isWorldwide = method === PAYMENT_METHODS.CARDWW1 || method === PAYMENT_METHODS.CARDWW2 || method === PAYMENT_METHODS.CARDWW3 || method === PAYMENT_METHODS.CARDWW4 || method === PAYMENT_METHODS.CARDWW5 || method === PAYMENT_METHODS.CARDWW6 || method === PAYMENT_METHODS.GOOGLE_PAY || method === PAYMENT_METHODS.APPLE_PAY || method === PAYMENT_METHODS.KZT_CARDWW4
  // Признак основного OverPay-провайдера (cardww2)
  const isWw2 = method === PAYMENT_METHODS.CARDWW2
  // Признак провайдера Fride (cardww3)
  const isWw3 = method === PAYMENT_METHODS.CARDWW3
  // Признак провайдера Softkeys (cardww4 / google_pay / apple_pay)
  const isWw4 = method === PAYMENT_METHODS.CARDWW4 || method === PAYMENT_METHODS.GOOGLE_PAY || method === PAYMENT_METHODS.APPLE_PAY || method === PAYMENT_METHODS.KZT_CARDWW4
  // Признак провайдера Paytree (cardww5)
  const isWw5 = method === PAYMENT_METHODS.CARDWW5
  // Признак провайдера EasyPay (cardww6)
  const isWw6 = method === PAYMENT_METHODS.CARDWW6
  // Выбор сервиса API в зависимости от провайдера
  const paymentService = isWw2 ? cardww2Api : isWw3 ? cardww3Api : isWw4 ? cardww4Api : isWw5 ? cardww5Api : isWw6 ? cardww6Api : isWorldwide ? cardwwApi : cardApi
  // Ключ цены пакета в /api/topup/
  const packagePriceKey = isWw2 ? 'cardww2' : isWw3 ? 'cardww3' : isWw4 ? 'cardww4' : isWw5 ? 'cardww5' : isWw6 ? 'cardww6' : isWorldwide ? 'cardww1' : 'card1'
  const minAmountKey = isWorldwide ? 'min_dollar' : 'min_rub'
  const maxAmountKey = isWorldwide ? 'max_dollar' : 'max_rub'

  // Состояния для табов
  const [activeTab, setActiveTab] = useState('packages') // packages | custom

  // Конфигурация card1 (курсы и лимиты)
  const [cardConfig, setCardConfig] = useState(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [configError, setConfigError] = useState(null)

  // Пакеты
  const [packages, setPackages] = useState([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [packagesError, setPackagesError] = useState(null)

  // Произвольная сумма
  const [rublesInput, setRublesInput] = useState('')
  const [crystalsInput, setCrystalsInput] = useState('')
  const [customAmountError, setCustomAmountError] = useState('')
  const [lastEdited, setLastEdited] = useState('rubles') // rubles | crystals

  // Обработка платежа
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingPackageId, setProcessingPackageId] = useState(null)

  useEffect(() => {
    setActiveTab('packages')
    setRublesInput('')
    setCrystalsInput('')
    setCustomAmountError('')
    setLastEdited('rubles')
    setCardConfig(null)
    setPackages([])
    setProcessingPackageId(null)
    loadCardConfig()
    loadPackages()
  }, [method])

  const rates = useMemo(() => {
    const priceOneCrystalInRub = parseFloat(cardConfig?.[isWorldwide ? 'price_one_crystal_in_dollar' : 'price_one_crystal_in_rub'] || '0')
    const priceOneRubInCrystal = parseFloat(cardConfig?.[isWorldwide ? 'price_one_dollar_in_crystal' : 'price_one_rub_in_crystal'] || '0')

    return {
      priceOneCrystalInRub,
      priceOneRubInCrystal
    }
  }, [cardConfig, isWorldwide])

  // Форматирование чисел с пробелами для отображения
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  const formatUsd = (value) => {
    const num = Number(value)
    if (!Number.isFinite(num)) return '$0.00'
    return `$${num.toFixed(2)}`
  }

  const normalizeUsdInput = (raw) => {
    const normalized = raw.replace(',', '.')
    const cleaned = normalized.replace(/[^0-9.]/g, '')
    const parts = cleaned.split('.')

    if (parts.length <= 1) return cleaned

    return `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`
  }

  const formatPaymentAmount = (value) => {
    return isWorldwide ? formatUsd(value) : `${formatNumber(value)} ₽`
  }

  const loadCardConfig = async () => {
    setIsLoadingConfig(true)
    setConfigError(null)

    try {
      // У каждого сервиса функции получения конфига названы по-разному:
      //   cardApi.fetchCardConfig, cardwwApi.fetchCardWwConfig, cardww2Api.fetchCardWw2Config, cardww3Api.fetchCardWw3Config
      const config =
        (await paymentService.fetchCardWw2Config?.()) ||
        (await paymentService.fetchCardWw3Config?.()) ||
        (await paymentService.fetchCardWw4Config?.()) ||
        (await paymentService.fetchCardWw5Config?.()) ||
        (await paymentService.fetchCardWw6Config?.()) ||
        (await paymentService.fetchCardWwConfig?.()) ||
        (await paymentService.fetchCardConfig?.())
      setCardConfig(config)
      console.log('✅ Card config loaded:', config)
    } catch (error) {
      console.error('❌ Error loading Card config:', error)
      setConfigError(t('payment.sbp.configError'))
    } finally {
      setIsLoadingConfig(false)
    }
  }

  const loadPackages = async () => {
    setIsLoadingPackages(true)
    setPackagesError(null)

    try {
      // Аналогично fetchConfig — функции получения пакетов названы по-разному у каждого сервиса
      const cardPackages =
        (await paymentService.fetchCardWw2Packages?.()) ||
        (await paymentService.fetchCardWw3Packages?.()) ||
        (await paymentService.fetchCardWw4Packages?.()) ||
        (await paymentService.fetchCardWw5Packages?.()) ||
        (await paymentService.fetchCardWw6Packages?.()) ||
        (await paymentService.fetchCardWwPackages?.()) ||
        (await paymentService.fetchCardPackages?.())

      // Сортируем пакеты: самый дешевый первый, затем популярные
      const sortedPackages = [...cardPackages].sort((a, b) => {
        const minPrice = Math.min(...cardPackages.map((p) => p[packagePriceKey]))

        if (a[packagePriceKey] === minPrice && b[packagePriceKey] !== minPrice) return -1
        if (b[packagePriceKey] === minPrice && a[packagePriceKey] !== minPrice) return 1

        if (a.popular && !b.popular) return -1
        if (b.popular && !a.popular) return 1

        return a[packagePriceKey] - b[packagePriceKey]
      })

      setPackages(sortedPackages)
      console.log('✅ Card packages loaded:', sortedPackages)
    } catch (error) {
      console.error('❌ Error loading Card packages:', error)
      setPackagesError(t('payment.sbp.packagesError'))
    } finally {
      setIsLoadingPackages(false)
    }
  }

  const openPaymentLink = (paymentLink, paymentWindow = null) => {
    // У каждого сервиса функция открытия ссылки имеет своё имя
    const openPaymentHandler = isWw2
      ? cardww2Api.openCardWw2Payment
      : isWw3
        ? cardww3Api.openCardWw3Payment
        : isWw4
          ? cardww4Api.openCardWw4Payment
          : isWw5
            ? cardww5Api.openCardWw5Payment
            : isWw6
              ? cardww6Api.openCardWw6Payment
              : isWorldwide
                ? cardwwApi.openCardWwPayment
                : cardApi.openCardPayment

    openPaymentHandler(paymentLink, () => {
      toast.success(t('payment.sbp.paymentNotification'), {
        duration: 4000,
        icon: '💳'
      })

      setTimeout(() => {
        navigate('/')
      }, 1500)
    }, paymentWindow)
  }

  const handlePackagePayment = async (pkg) => {
    if (isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)
    setProcessingPackageId(pkg.id)

    try {
      console.log('💰 Создание платежа Card для пакета:', pkg)

      // Для пакетов используем быстрый платеж в валюте выбранного метода.
      // У всех трёх сервисов есть createPaymentByDollars / createPaymentByRubles,
      // поэтому обращаемся через общую переменную paymentService.
      // Для cardww4/cardww5/cardww6 всегда отправляем crystal, чтобы числа в /api/topup/ и на странице оплаты совпадали
      const { link } = (isWw4 || isWw5 || isWw6)
        ? await paymentService.createPaymentByCrystals(pkg.crystals)
        : isWorldwide
          ? await paymentService.createPaymentByDollars(pkg[packagePriceKey])
          : await paymentService.createPaymentByRubles(pkg[packagePriceKey])
      openPaymentLink(link, paymentWindow)
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа Card:', error)

      if (error.message === 'INVALID_AMOUNT') {
        toast.error(isWorldwide
          ? t('payment.crypto.errorInvalidAmountUsd', {
              min: cardConfig?.min_dollar ?? 10,
              max: cardConfig?.max_dollar ?? 5000
            })
          : t('payment.sbp.errorInvalidAmount', {
              min: cardConfig?.min_rub ?? 100,
              max: cardConfig?.max_rub ?? 50000
            }))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else if (isWw6 && error.message === 'PAYMENT_LIMIT') {
        toast.error(t('payment.uzs2.errorLimitOrder'))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
      setProcessingPackageId(null)
    }
  }

  const handleRublesChange = (e) => {
    // Для card1 разрешаем только целые рубли, для cardww1 — дробные доллары
    const value = isWorldwide ? normalizeUsdInput(e.target.value) : e.target.value.replace(/[^\d]/g, '')
    setLastEdited('rubles')
    setRublesInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneRubInCrystal) {
      const rubles = parseFloat(value)
      if (Number.isFinite(rubles)) {
        // cardww6 считает кристаллы через floor, как бэкенд
        const crystals = isWw6
          ? Math.floor(Math.round(rubles * 100) / 100 * rates.priceOneRubInCrystal)
          : Math.round(rubles * rates.priceOneRubInCrystal)
        setCrystalsInput(crystals.toString())
      } else {
        setCrystalsInput('')
      }
    } else {
      setCrystalsInput('')
    }
  }

  const handleCrystalsChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '')
    setLastEdited('crystals')
    setCrystalsInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneCrystalInRub) {
      const crystals = parseFloat(value)
      if (Number.isFinite(crystals)) {
        const rubles = isWorldwide
          ? (crystals * rates.priceOneCrystalInRub).toFixed(2)
          : Math.round(crystals * rates.priceOneCrystalInRub).toString()
        setRublesInput(rubles.toString())
      } else {
        setRublesInput('')
      }
    } else {
      setRublesInput('')
    }
  }

  const validateCustomAmount = () => {
    if (!rublesInput || !cardConfig) {
      setCustomAmountError(t('payment.sbp.enterAmount'))
      return false
    }

    const rubles = parseFloat(rublesInput)
    if (!Number.isFinite(rubles)) {
      setCustomAmountError(isWorldwide ? t('payment.crypto.enterCorrectAmount') : t('payment.sbp.enterAmount'))
      return false
    }

    if (rubles < cardConfig[minAmountKey] || rubles > cardConfig[maxAmountKey]) {
      setCustomAmountError(isWorldwide
        ? t('payment.crypto.errorInvalidAmountUsd', {
            min: cardConfig[minAmountKey],
            max: cardConfig[maxAmountKey]
          })
        : t('payment.sbp.errorInvalidAmount', {
            min: formatNumber(cardConfig[minAmountKey]),
            max: formatNumber(cardConfig[maxAmountKey])
          }))
      return false
    }

    return true
  }

  const handleCustomPayment = async () => {
    if (!validateCustomAmount() || isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)

    try {
      const rubles = isWorldwide ? parseFloat(rublesInput || '0') : parseInt(rublesInput || '0', 10)
      const crystals = parseInt(crystalsInput || '0', 10)

      console.log('💰 Создание платежа Card (custom):', { rubles, crystals, lastEdited })

      // Для cardww4/cardww5 (Softkeys/Paytree) всегда отправляем crystal:
      //   - если вводили кристаллы — берём как есть
      //   - если вводили доллары — переводим floor(dollar × price_one_dollar_in_crystal)
      const payment = (isWw4 || isWw5)
        ? await paymentService.createPaymentByCrystals(
            lastEdited === 'crystals'
              ? crystals
              : Math.floor(rubles * rates.priceOneRubInCrystal)
          )
        : lastEdited === 'crystals'
          ? await paymentService.createPaymentByCrystals(crystals)
          : isWorldwide
            ? await paymentService.createPaymentByDollars(rubles)
            : await paymentService.createPaymentByRubles(rubles)

      openPaymentLink(payment.link, paymentWindow)
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа Card:', error)

      if (error.message === 'INVALID_AMOUNT') {
        setCustomAmountError(isWorldwide
          ? t('payment.crypto.errorInvalidAmountUsd', {
              min: cardConfig?.min_dollar ?? 10,
              max: cardConfig?.max_dollar ?? 5000
            })
          : t('payment.sbp.errorInvalidAmount', {
              min: formatNumber(cardConfig?.min_rub ?? 100),
              max: formatNumber(cardConfig?.max_rub ?? 50000)
            }))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else if (isWw6 && error.message === 'PAYMENT_LIMIT') {
        toast.error(t('payment.uzs2.errorLimitOrder'))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const renderPackages = () => {
    if (isLoadingPackages) {
      return (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="bg-surface rounded-lg border border-white border-opacity-10 p-4 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg"></div>
                  <div>
                    <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-16"></div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                  <div className="h-6 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (packagesError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{packagesError}</p>
          <button
            onClick={loadPackages}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('payment.sbp.retry')}
          </button>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-4">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`relative bg-surface rounded-lg border border-white border-opacity-10 p-4 cursor-pointer transition-all hover:border-accent-primary/50 hover:scale-[1.02] active:scale-[0.98] ${
              pkg.popular ? 'ring-2 ring-accent-primary ring-opacity-50' : ''
            } ${
              isProcessing && processingPackageId === pkg.id
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={() => !isProcessing && handlePackagePayment(pkg)}
          >
            {pkg.popular && (
              <div className="absolute -top-2 -right-2">
                <span className="inline-block px-3 py-1 bg-accent-primary text-dark-primary text-xs font-bold rounded-full shadow-lg">
                  {t('payment.popular')}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gem size={28} className="text-dark-primary" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{formatNumber(pkg.crystals)}</p>
                  <p className="text-text-secondary text-sm">{t('payment.crystals')}</p>
                  {/* Бонусные кристаллы от промокода */}
                  {pkg.promo_bonus_crystals > 0 && (
                    <p className="text-accent-primary text-xs font-semibold mt-0.5">
                      +{formatNumber(pkg.promo_bonus_crystals)} {t('promo.bonus')}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right">
                {isProcessing && processingPackageId === pkg.id ? (
                  <div className="flex items-center gap-2 text-accent-primary">
                    <Loader size={20} className="animate-spin" />
                  </div>
                ) : (
                  <div className="text-accent-primary font-bold text-lg">
                    <span>{formatPaymentAmount(pkg[packagePriceKey])}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderCustomAmount = () => {
    if (isLoadingConfig) {
      return (
        <div className="text-center py-8">
          <Loader size={48} className="text-accent-primary mx-auto mb-4 animate-spin" />
          <p className="text-text-secondary">{t('payment.sbp.loadingConfig')}</p>
        </div>
      )
    }

    if (configError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{configError}</p>
          <button
            onClick={loadCardConfig}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('payment.sbp.retry')}
          </button>
        </div>
      )
    }

    const hasError = customAmountError !== ''
    const rubles = parseFloat(rublesInput)
    const isValidAmount =
      rublesInput &&
      !hasError &&
      Number.isFinite(rubles) &&
      rubles >= cardConfig[minAmountKey] &&
      rubles <= cardConfig[maxAmountKey]

    return (
      <div className="space-y-6">
        <div className="text-center">
          <p className="text-text-secondary text-sm">
            {isWorldwide
              ? t('payment.crypto.limits', {
                  min: cardConfig[minAmountKey],
                  max: cardConfig[maxAmountKey]
                })
              : t('payment.sbp.minMax', {
                  min: formatNumber(cardConfig[minAmountKey]),
                  max: formatNumber(cardConfig[maxAmountKey])
                })}
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{isWorldwide ? t('payment.crypto.amountUsd') : t('payment.sbp.rubles')}</label>
          <input
            type="text"
            inputMode={isWorldwide ? 'decimal' : 'numeric'}
            value={rublesInput}
            onChange={handleRublesChange}
            placeholder={isWorldwide ? t('payment.crypto.placeholderUsd') : t('payment.sbp.enterAmount')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{isWorldwide ? t('payment.crypto.crystals') : t('payment.sbp.crystalsAmount')}</label>
          <input
            type="text"
            inputMode="numeric"
            value={crystalsInput}
            onChange={handleCrystalsChange}
            placeholder={isWorldwide ? t('payment.crypto.placeholderCrystals') : t('payment.sbp.enterCrystals')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        {/* Промо-бонус для произвольной суммы */}
        {cardConfig?.promo?.percent && crystalsInput && (
          <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-lg p-3 text-center">
            <span className="text-accent-primary font-semibold text-sm">
              +{formatNumber(Math.ceil(parseInt(crystalsInput) * cardConfig.promo.percent / 100))} {t('promo.bonus')}
            </span>
          </div>
        )}

        {hasError && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm text-center">{customAmountError}</p>
          </div>
        )}

        <button
          onClick={handleCustomPayment}
          disabled={!isValidAmount || isProcessing}
          className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
            isValidAmount && !isProcessing
              ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader size={20} className="animate-spin" />
              {t('payment.sbp.paying')}
            </span>
          ) : (
            `${t('payment.sbp.pay')} ${rublesInput ? formatPaymentAmount(rublesInput) : (isWorldwide ? '$0.00' : '0 ₽')}`
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('packages')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'packages'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Package size={18} className="inline mr-2" />
            {isWorldwide ? t('payment.crypto.tabPackages') : t('payment.sbp.tabPackages')}
          </button>

          <button
            onClick={() => setActiveTab('custom')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'custom'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Edit3 size={18} className="inline mr-2" />
            {isWorldwide ? t('payment.crypto.tabCustom') : t('payment.sbp.tabCustom')}
          </button>
        </div>
      </div>

      {activeTab === 'packages' ? renderPackages() : renderCustomAmount()}
    </div>
  )
}

export default CardPayment
